// Chat lines filed to a forecast before its row exists.
//
// A submitted forecast has no row until its trace is allocated, and `persistMessage` cannot save
// a line without one. A line filed in that window (a draft question's answer that followed the
// question into the new forecast) is held here, by conversation, and saved once the row exists
// (submitForecast's `persistRunOnTrace`). Without that it shows in-session and is gone on reload.
//
// A held line lives only as long as its conversation in the run-store: removing the run (which
// deletes a root's conversation) or resetting the store discards it.
import { persistMessage } from "../lib/messages";
import { entryToMessageBody } from "../lib/forecast/hydrate.js";
import type { Entry } from "../lib/forecast/model.js";
import type { RunState } from "../lib/forecast/reducer.js";
import { runStore } from "./runStore.js";

const held = new Map<string, Entry[]>();
let unsubscribe: (() => void) | null = null;

function stopWatching(): void {
  unsubscribe?.();
  unsubscribe = null;
}

function discardOrphans(state: RunState): void {
  for (const convId of held.keys()) {
    if (!state.conversationsById[convId]) held.delete(convId);
  }
  if (held.size === 0) stopWatching();
}

/** Save `entry` to `forecastId`'s row, or hold it for conversation `convId` until it has one. */
export function persistOrHold(convId: string, forecastId: string | null, entry: Entry): void {
  if (forecastId) {
    persistMessage(forecastId, entryToMessageBody(entry));
    return;
  }
  held.set(convId, [...(held.get(convId) ?? []), entry]);
  unsubscribe ??= runStore.subscribe(discardOrphans);
}

/** Save every line held for `convId` to its newly allocated row, in the order they were filed. */
export function flushHeld(convId: string, forecastId: string): void {
  const entries = held.get(convId);
  if (!entries) return;
  held.delete(convId);
  if (held.size === 0) stopWatching();
  for (const entry of entries) persistMessage(forecastId, entryToMessageBody(entry));
}

/** Test-only: forget everything held. */
export function __resetHeldForTests(): void {
  held.clear();
  stopWatching();
}
